import React from 'react';
import { createLogo, createSealOrSign } from '../../utils/imageUtils';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';

function formatINR(num) {
  if (isNaN(num)) return '';
  return Number(num).toLocaleString('en-IN');
}

function numberToWordsIndian(num) {
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  }
  return inWords(Math.floor(num));
}
function numToWords(num) {
  return numberToWordsIndian(num) + ' Rupees Only';
}

function DebitNoteTemplate({ billData = {}, companyDetails = {}, partyDetails = {}, bankDetails = {} }) {
  const visibleItems = (billData.items || billData.rows || []).filter(it => {
    const hasDesc = !!(it.description && String(it.description).trim());
    const hasItem = !!(it.item && String(it.item).trim());
    const hasNumbers = (parseFloat(it.qty) || 0) > 0 || (parseFloat(it.rate) || 0) > 0 || (parseFloat(it.amount) || 0) > 0 || (parseFloat(it.total) || 0) > 0;
    return hasDesc || hasItem || hasNumbers;
  });
  
  const subtotal = visibleItems.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const discount = billData.discountType === 'percent'
    ? subtotal * (parseFloat(billData.discountValue) || 0) / 100
    : parseFloat(billData.discountValue) || 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const totalSGST = visibleItems.reduce((sum, row) => sum + ((parseFloat(row.amount) || 0) * ((parseFloat(row.sgst) || 0) / 100)), 0);
  const totalCGST = visibleItems.reduce((sum, row) => sum + ((parseFloat(row.amount) || 0) * ((parseFloat(row.cgst) || 0) / 100)), 0);
  const totalIGST = visibleItems.reduce((sum, row) => sum + ((parseFloat(row.amount) || 0) * ((parseFloat(row.igst) || 0) / 100)), 0);
  const grandTotal = discountedSubtotal + totalSGST + totalCGST + totalIGST;

  return (
    <div className="p-4 md:p-8 bg-white rounded shadow max-w-3xl w-full mx-auto text-sm print:p-0 print:shadow-none print:bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            {createLogo(companyDetails.logoUrl, companyDetails.firmName || 'Company', 'h-16 w-auto')}
          </div>
          <div>
            <div className="text-xl font-bold uppercase">{companyDetails.firmName}</div>
            <div>{companyDetails.address}{companyDetails.city ? ', ' + companyDetails.city : ''}{companyDetails.pincode ? ' - ' + companyDetails.pincode : ''}</div>
            <div>{companyDetails.state}</div>
            <div>GSTIN: {companyDetails.gstin || 'N/A'}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tracking-wider text-blue-600">DEBIT NOTE</div>
          <div className="mt-2"><span className="font-semibold">Note No:</span> {billData.number || ''}</div>
          <div><span className="font-semibold">Date:</span> {formatDateToCompanyFormat(billData.invoiceDate || billData.billDate || billData.date, companyDetails)}</div>
          {billData.originalBillNumber && (
            <div className="text-sm text-gray-600 mt-1">
              <span className="font-semibold">Original Bill:</span> {billData.originalBillNumber}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-2">
        <div>
          <div className="font-semibold">Debited To (Party):</div>
          <div>{partyDetails.firmName || partyDetails.name || ''}</div>
          <div>{partyDetails.address || ''}</div>
          <div>{partyDetails.city || ''} {partyDetails.pincode || ''}</div>
          <div>GSTIN: {partyDetails.gstin || 'N/A'}</div>
        </div>
        <div className="text-right">
          <div className="font-semibold">Debited By:</div>
          <div>{companyDetails.firmName}</div>
          <div>{companyDetails.address}</div>
          <div>GSTIN: {companyDetails.gstin || 'N/A'}</div>
        </div>
      </div>
      {billData.notes && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="font-semibold text-blue-800">Note:</div>
          <div className="text-blue-700">{billData.notes}</div>
        </div>
      )}
      <div className="overflow-x-auto mt-4 mb-4">
        <table className="w-full min-w-[640px] border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Sr.</th>
              <th className="border px-2 py-1">Description</th>
              <th className="border px-2 py-1">HSN</th>
              <th className="border px-2 py-1">Qty</th>
              <th className="border px-2 py-1">Rate</th>
              <th className="border px-2 py-1">Amount</th>
              <th className="border px-2 py-1">GST %</th>
              <th className="border px-2 py-1">Total</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((row, idx) => (
              <tr key={idx}>
                <td className="border px-2 py-1 text-center">{idx + 1}</td>
                <td className="border px-2 py-1">{row.description || row.itemName || ''}</td>
                <td className="border px-2 py-1 text-center">{row.hsn || ''}</td>
                <td className="border px-2 py-1 text-right">{row.qty || row.nos || ''} {row.unit || 'Nos.'}</td>
                <td className="border px-2 py-1 text-right">₹{formatINR(row.rate)}</td>
                <td className="border px-2 py-1 text-right">₹{formatINR(row.amount)}</td>
                <td className="border px-2 py-1 text-center">{(parseFloat(row.sgst || 0) + parseFloat(row.cgst || 0) + parseFloat(row.igst || 0)) || ''}</td>
                <td className="border px-2 py-1 text-right">₹{formatINR(row.total || row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {companyDetails.gstinType === 'Regular' ? (
        <div className="flex flex-row gap-8 mt-4 mb-2">
          <div className="flex-1">
            <div className="font-bold mb-1">Summary:</div>
            <div>Taxable Amount: ₹{formatINR(subtotal)}</div>
            <div>SGST: ₹{formatINR(totalSGST)}</div>
            <div>CGST: ₹{formatINR(totalCGST)}</div>
            <div>IGST: ₹{formatINR(totalIGST)}</div>
            <div className="font-bold text-blue-600">Debit Amount: ₹{formatINR(grandTotal)}</div>
            <div>In Words: {numToWords(grandTotal)}</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-8 mt-4 mb-2">
          <div className="flex-1">
            <div className="font-bold mb-1">Summary:</div>
            <div className="font-bold text-blue-600">Debit Amount: ₹{formatINR(discountedSubtotal)}</div>
            <div>In Words: {numToWords(discountedSubtotal)}</div>
          </div>
        </div>
      )}
      <div className="flex flex-row items-end justify-end gap-8 mt-8 mb-2">
        {companyDetails.sealUrl && (
          <div className="flex flex-col items-center mr-4">
            {createSealOrSign(companyDetails.sealUrl, 'Company Seal')}
            <div className="text-xs text-gray-500">Company Seal</div>
          </div>
        )}
        {companyDetails.signUrl && (
          <div className="flex flex-col items-center">
            {createSealOrSign(companyDetails.signUrl, 'Authorised Signatory')}
            <div className="text-xs text-gray-500">Authorised Signatory</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DebitNoteTemplate;

